import type { ApiSchemas } from '@rental/contracts/client'
import { format, formatDateTime } from '@rental/i18n'
import { Button, type Tone } from '@rental/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getRouteApi, Link } from '@tanstack/react-router'
import { ChevronLeft } from 'lucide-react'
import { Fragment, type ReactNode, type RefObject, useEffect, useId, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { can } from '../access'
import { useAccess, useSession } from '../access-context'
import { OutcomeNotice } from '../components/OutcomeNotice'
import { PageHeader } from '../components/PageHeader'
import { ListRow } from '../components/Results'
import { Section } from '../components/Section'
import { StatusChip } from '../components/StatusChip'
import { Guard, RecordPending } from '../components/states'
import { EmailText, Facts, ProblemNotice } from '../components/ui'
import { useLocale } from '../i18n'
import {
  DeactivateDialog,
  KeyDialog,
  PasswordDialog,
  ReactivateDialog,
} from '../people/AccessDialogs'
import { type EditOutcome, EditPerson } from '../people/EditPerson'
import { type PasswordState, type Person, type PersonDevice, passwordState } from '../people/people'
import { loadPeople, loadPerson, PEOPLE_KEY, personChanged, personKey } from '../people/queries'
import { ShownOnce } from '../people/ShownOnce'
import { problemOf, type StaffProblem } from '../problems'
import { PersonChips } from './People'

const route = getRouteApi('/people/$userId')

type Detail = ApiSchemas['StaffPersonDetail']

/** S9 (ADM-1, Q-51): one person, for the holders of users.manage. */
export function PersonScreen() {
  const { messages: m } = useLocale()
  const { access } = useAccess()
  return (
    <Guard allowed={can(access, 'users.manage')} title={m.person.title}>
      <PersonRecord />
    </Guard>
  )
}

function PersonRecord() {
  const { messages: m } = useLocale()
  const { userId } = route.useParams()
  const detail = useQuery({ queryKey: personKey(userId), queryFn: () => loadPerson(userId) })
  const problem = problemOf(detail.error)
  const back = (
    <Link to="/people" className="flex min-h-touch items-center gap-1 self-start">
      <ChevronLeft aria-hidden="true" className="size-4" />
      {m.person.back}
    </Link>
  )
  if (!detail.data) {
    return (
      <RecordPending
        back={back}
        title={m.person.title}
        problem={problem}
        notFound={m.person.notFound}
      />
    )
  }
  return <PersonPage detail={detail.data} back={back} problem={problem} />
}

/** An outcome said once per action: a new key each time, so each takes the focus (UI-2g). */
type Said = { key: number; tone: Tone; text: string }

/** A password or a key just made, with what made it: on this page until "Fatto", or until it is left. */
type Shown = {
  key: number
  text: string
  /** Where "Fatto" returns: a new role's key to Modifica, the rest to the access buttons. */
  from: 'details' | 'access'
  password: ApiSchemas['OneTimePassword'] | null
  authenticator: ApiSchemas['AuthenticatorKey'] | null
}

/**
 * Who the person is and what they may do, their access (given again with the
 * Owner's code, taken away without it) and the devices they have worked on.
 * On your own page your name and language change; your role and access are
 * another Owner's, and your password is changed from "Cambia password".
 */
function PersonPage({
  detail,
  back,
  problem,
}: {
  detail: Detail
  back: ReactNode
  /** A refresh that failed: said, while the person as last read stays. */
  problem: StaffProblem | null
}) {
  const { locale, messages: m } = useLocale()
  const copy = m.person
  const queryClient = useQueryClient()
  const { transition } = useAccess()
  const session = useSession()
  const { person, devices } = detail
  const roles = useQuery({ queryKey: PEOPLE_KEY, queryFn: loadPeople })
  const editId = useId()
  const accessActions = useRef<HTMLDivElement>(null)
  const [said, setSaid] = useState<Said | null>(null)
  const [shown, setShown] = useState<Shown | null>(null)
  const password = passwordState(person, Date.now())
  const role = (m.roles as Readonly<Record<string, string>>)[person.role] ?? person.role

  function say(text: string, tone: Tone = 'success') {
    setShown(null)
    setSaid({ key: Date.now(), tone, text })
  }

  function show(text: string, secret: Pick<Shown, 'from' | 'password' | 'authenticator'>) {
    setSaid(null)
    setShown({ key: Date.now(), text, ...secret })
  }

  function edited(outcome: EditOutcome) {
    if (outcome.kind === 'conflict') {
      void queryClient.invalidateQueries({ queryKey: PEOPLE_KEY })
      say(copy.conflict, 'warning')
      return
    }
    personChanged(queryClient, outcome.person)
    // Your own name and language: the bar that says who is unlocked says it at once.
    if (outcome.person.self) {
      transition({
        state: 'unlocked',
        session: {
          ...session,
          user: {
            ...session.user,
            displayName: outcome.person.displayName,
            locale: outcome.person.locale,
          },
        },
      })
    }
    if (outcome.authenticator)
      show(copy.saved, { from: 'details', password: null, authenticator: outcome.authenticator })
    else say(copy.saved)
  }

  return (
    <>
      <PageHeader
        back={back}
        title={person.displayName}
        status={<PersonChips person={person} password={password} />}
        meta={role}
      >
        {/* What the chips say, with its day: when the account stopped, or until when the
            password someone else chose works. */}
        {person.disabledAt !== null ? (
          <p className="text-sm">
            {format(m.people.disabledOn, { date: formatDateTime(locale, person.disabledAt) })}
          </p>
        ) : person.passwordTemporaryUntil !== null && password !== 'own' ? (
          <p className="text-sm">
            {format(password === 'expired' ? m.people.expiredOn : m.people.temporaryUntil, {
              date: formatDateTime(locale, person.passwordTemporaryUntil),
            })}
          </p>
        ) : null}
      </PageHeader>
      <ProblemNotice problem={problem} />
      {said ? (
        <OutcomeNotice key={said.key} tone={said.tone}>
          <p>{said.text}</p>
        </OutcomeNotice>
      ) : null}
      {shown ? (
        <Fragment key={shown.key}>
          <OutcomeNotice>
            <p>{shown.text}</p>
          </OutcomeNotice>
          <ShownOnce
            name={person.displayName}
            password={shown.password}
            authenticator={shown.authenticator}
            onDone={() => {
              // Back to what made it, rather than to nothing.
              const from = shown.from
              flushSync(() => setShown(null))
              if (from === 'details') document.getElementById(editId)?.focus()
              else accessActions.current?.querySelector('button')?.focus()
            }}
          />
        </Fragment>
      ) : null}
      <DetailsSection
        person={person}
        roles={roles.data?.roles ?? null}
        editId={editId}
        onEdited={edited}
      />
      <AccessSection
        person={person}
        devices={devices}
        password={password}
        actions={accessActions}
        onSaid={say}
        onShown={(text, secret) => show(text, { from: 'access', ...secret })}
      />
      <DevicesSection devices={devices} />
    </>
  )
}

/** The person's details, and "Modifica" once the roles to offer are in. */
function DetailsSection({
  person,
  roles,
  editId,
  onEdited,
}: {
  person: Person
  roles: ApiSchemas['StaffRoleOption'][] | null
  /** Modifica's id: the page returns the focus to it after a key it made. */
  editId: string
  onEdited: (outcome: EditOutcome) => void
}) {
  const { locale, messages: m } = useLocale()
  const copy = m.person
  const [editing, setEditing] = useState(false)
  const backToEdit = useRef(false)
  useEffect(() => {
    if (editing || !backToEdit.current) return
    backToEdit.current = false
    document.getElementById(editId)?.focus()
  }, [editing, editId])
  const role = (m.roles as Readonly<Record<string, string>>)[person.role] ?? person.role

  return (
    <Section
      title={copy.details}
      actions={
        !editing && roles ? (
          <Button
            id={editId}
            variant="outline"
            className="rounded-sm"
            onClick={() => setEditing(true)}
          >
            {copy.edit}
          </Button>
        ) : null
      }
    >
      {editing && roles ? (
        <EditPerson
          person={person}
          roles={roles}
          onDone={(outcome) => {
            setEditing(false)
            onEdited(outcome)
          }}
          onCancel={() => {
            backToEdit.current = true
            setEditing(false)
          }}
        />
      ) : (
        <Facts
          items={[
            [copy.name, person.displayName],
            [copy.email, <EmailText key="email" address={person.email} />],
            [copy.role, role],
            [copy.locale, m.languages[person.locale]],
            [
              copy.authenticator,
              person.authenticator ? copy.authenticatorSet : copy.authenticatorNone,
            ],
            [
              copy.lastUnlock,
              person.lastUnlockAt ? formatDateTime(locale, person.lastUnlockAt) : copy.never,
            ],
          ]}
        />
      )}
    </Section>
  )
}

/**
 * Access: a new password, a new key (for a role that signs in with a code),
 * a deactivation; for a deactivated person, the reactivation. Each opens a
 * question first (D11). On your own page, none: where your own password is
 * changed, and that the rest is another Owner's.
 */
function AccessSection({
  person,
  devices,
  password,
  actions,
  onSaid,
  onShown,
}: {
  person: Person
  devices: readonly PersonDevice[]
  password: PasswordState
  actions: RefObject<HTMLDivElement | null>
  onSaid: (text: string) => void
  onShown: (text: string, secret: Pick<Shown, 'password' | 'authenticator'>) => void
}) {
  const { messages: m } = useLocale()
  const copy = m.person
  const queryClient = useQueryClient()
  const passwordDialog = useRef<HTMLDialogElement>(null)
  const keyDialog = useRef<HTMLDialogElement>(null)
  const deactivateDialog = useRef<HTMLDialogElement>(null)
  const reactivateDialog = useRef<HTMLDialogElement>(null)
  const name = person.displayName

  if (person.self) {
    return (
      <Section title={copy.access}>
        <p>{copy.selfAccess}</p>
        <Link to="/password" className="inline-flex min-h-touch items-center self-start">
          {m.nav.changePassword}
        </Link>
      </Section>
    )
  }

  return (
    <Section title={copy.access}>
      <p className="text-sm text-text-muted">{copy.accessIntro}</p>
      <div ref={actions} className="flex flex-wrap gap-3">
        {person.disabledAt === null ? (
          <>
            <Button
              variant="outline"
              className="rounded-sm"
              onClick={() => passwordDialog.current?.showModal()}
            >
              {copy.newPassword}
            </Button>
            {person.authenticator ? (
              <Button
                variant="outline"
                className="rounded-sm"
                onClick={() => keyDialog.current?.showModal()}
              >
                {copy.newKey}
              </Button>
            ) : null}
            <Button variant="staff" onClick={() => deactivateDialog.current?.showModal()}>
              {copy.deactivate}
            </Button>
          </>
        ) : (
          <Button variant="staff" onClick={() => reactivateDialog.current?.showModal()}>
            {copy.reactivate}
          </Button>
        )}
      </div>
      {person.disabledAt === null ? (
        <>
          <PasswordDialog
            dialogRef={passwordDialog}
            person={person}
            onDone={(answer) => {
              personChanged(queryClient, answer.person)
              onShown(format(copy.passwordDone, { name }), {
                password: answer.password,
                authenticator: null,
              })
            }}
          />
          {person.authenticator ? (
            <KeyDialog
              dialogRef={keyDialog}
              person={person}
              onDone={(answer) => {
                personChanged(queryClient, answer.person)
                onShown(format(copy.keyDone, { name }), {
                  password: null,
                  authenticator: answer.authenticator,
                })
              }}
            />
          ) : null}
          <DeactivateDialog
            dialogRef={deactivateDialog}
            person={person}
            devices={devices}
            onDone={(answer) => {
              personChanged(queryClient, answer)
              onSaid(format(copy.deactivateDone, { name }))
            }}
          />
        </>
      ) : (
        <ReactivateDialog
          dialogRef={reactivateDialog}
          person={person}
          expired={password === 'expired'}
          onDone={(answer) => {
            personChanged(queryClient, answer)
            onSaid(format(copy.reactivateDone, { name }))
          }}
        />
      )}
    </Section>
  )
}

/** The devices the person has worked on: enrolled, or unlocked at least once. */
function DevicesSection({ devices }: { devices: readonly PersonDevice[] }) {
  const { locale, messages: m } = useLocale()
  const copy = m.person
  return (
    <Section title={copy.devices}>
      {devices.length === 0 ? (
        <p className="text-text-muted">{copy.devicesNone}</p>
      ) : (
        <ul className="flex flex-col">
          {devices.map((device) => (
            <ListRow
              key={device.id}
              still
              title={<span className="font-bold">{device.name}</span>}
              end={
                <>
                  {device.current ? (
                    <StatusChip tone="info">{copy.deviceCurrent}</StatusChip>
                  ) : null}
                  {device.revokedAt ? (
                    <StatusChip tone="neutral">
                      {format(copy.deviceRevoked, {
                        date: formatDateTime(locale, device.revokedAt),
                      })}
                    </StatusChip>
                  ) : null}
                </>
              }
            >
              <span>
                {device.lastUsedAt
                  ? format(copy.deviceLastUsed, {
                      date: formatDateTime(locale, device.lastUsedAt),
                    })
                  : copy.deviceNeverUnlocked}
              </span>
              {device.onlyThisPerson ? <span>{copy.deviceOnly}</span> : null}
            </ListRow>
          ))}
        </ul>
      )}
    </Section>
  )
}
